import { FC, ReactNode, useCallback, useEffect, useRef, useState } from "react";
import { BoundsObserver } from "../../utilities/BoundsObserver";
import { Shelf } from "capitalrx-components-2.0/lib/layout";
import classNames from "classnames";

/**
 * The `Overlay` component properties.
 */
export interface OverlayProps {
  /**
   * Content to show after the text.
   */
  readonly prefix?: ReactNode;

  /**
   * Content to show prior to the text.
   */
  readonly suffix?: ReactNode;

  /**
   * The function to call when the layout changes.
   */
  readonly onLayout?: (prefixSize: number | null, suffixSize: number | null) => void;
}

/**
 * The text overlay which displays the prefix and suffix.
 */
export const Overlay: FC<OverlayProps> = (props) => {
  const { prefix, suffix, onLayout } = props;

  const prefixRef = useRef<HTMLDivElement | null>(null);
  const suffixRef = useRef<HTMLDivElement | null>(null);
  const [prefixSize, setPrefixSize] = useState<number | null>(null);
  const [suffixSize, setSuffixSize] = useState<number | null>(null);

  const [boundsObserver] = useState(
    () =>
      new BoundsObserver((entries) => {
        for (const entry of entries) {
          if (entry.target === prefixRef.current) setPrefixSize(entry.bounds.width);
          if (entry.target === suffixRef.current) setSuffixSize(entry.bounds.width);
        }
      }),
  );

  useEffect(() => {
    if (!prefix) setPrefixSize(null);
    if (!suffix) setSuffixSize(null);
    if (!prefix && !suffix) return;

    boundsObserver.resume();

    return () => {
      boundsObserver.pause();
    };
  }, [prefix, suffix]);

  useEffect(() => {
    return () => {
      boundsObserver.disconnect();
    };
  }, []);

  const prefixRefHandler = useCallback((ref: HTMLDivElement | null) => {
    if (prefixRef.current) boundsObserver.unobserve(prefixRef.current);
    prefixRef.current = ref;
    if (ref) boundsObserver.observe(ref);
  }, []);

  const suffixRefHandler = useCallback((ref: HTMLDivElement | null) => {
    if (suffixRef.current) boundsObserver.unobserve(suffixRef.current);
    suffixRef.current = ref;
    if (ref) boundsObserver.observe(ref);
  }, []);

  useEffect(() => {
    onLayout?.(prefix && prefixSize !== null ? prefixSize : null, suffix && suffixSize !== null ? suffixSize : null);
  }, [onLayout, prefix, suffix, prefixSize, suffixSize]);

  if (!prefix && !suffix) return null;

  return (
    <Shelf className="overlay" spacing="none" alignment="span">
      {prefix && (
        <div ref={prefixRefHandler} className={classNames("prefix", { hidden: prefixSize === null })}>
          {prefix}
        </div>
      )}
      <Shelf.Spacer />
      {suffix && (
        <div ref={suffixRefHandler} className={classNames("suffix", { hidden: suffixSize === null })}>
          {suffix}
        </div>
      )}
    </Shelf>
  );
};
